import React, { useEffect, useState, useCallback } from 'react';
import {
    Switch, Select, Checkbox, Table, Tag, Spin, Button, Tooltip, Badge,
} from 'antd';
import {
    MailOutlined, ClockCircleOutlined, GlobalOutlined,
    CheckCircleOutlined, ExclamationCircleOutlined, SyncOutlined,
    CloseCircleOutlined, MinusCircleOutlined, InfoCircleOutlined,
} from '@ant-design/icons';
import { ApiCaller, Utils, Colors } from '../../config';
import useColors from '../../hooks/useColors';
import useDateTime from '../../hooks/useDateTime';

// ─── constants ───────────────────────────────────────────────────────────────

const REPORT_TYPE_LABELS = {
    daily_operations: 'Daily Operations',
    incidents: 'Incidents',
    attendance: 'Attendance',
    open_shifts: 'Open Shifts',
};

const ROLE_LABELS = {
    admin: 'Admin',
    manager: 'Manager',
    supervisor: 'Supervisor',
};

const ALL_REPORT_TYPES = ['daily_operations', 'incidents', 'attendance', 'open_shifts'];
const ALL_ROLES = ['admin', 'manager', 'supervisor'];

const TIMEZONES = [
    'UTC',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Phoenix',
    'America/Anchorage',
    'America/Honolulu',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Asia/Kolkata',
    'Asia/Dubai',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Australia/Sydney',
    'Pacific/Auckland',
];

const STATUS_MAP = {
    pending:  { color: '#EEB600', icon: <ClockCircleOutlined />,      label: 'Pending'  },
    queued:   { color: '#1890ff', icon: <SyncOutlined spin />,         label: 'Queued'   },
    sent:     { color: '#52c41a', icon: <CheckCircleOutlined />,       label: 'Sent'     },
    partial:  { color: '#fa8c16', icon: <ExclamationCircleOutlined />, label: 'Partial'  },
    failed:   { color: '#ff4d4f', icon: <CloseCircleOutlined />,       label: 'Failed'   },
};

// ─── helper: hours array for time select ─────────────────────────────────────
const TIME_OPTIONS = [];
for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
        const hh = String(h).padStart(2, '0');
        const mm = String(m).padStart(2, '0');
        TIME_OPTIONS.push({ value: `${hh}:${mm}`, label: `${hh}:${mm}` });
    }
}

// ─── styles ──────────────────────────────────────────────────────────────────
const card = {
    background: '#fff',
    border: '1px solid #e0e4e8',
    borderRadius: 10,
    padding: '22px 24px',
    marginBottom: 22,
    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
};

const sectionTitle = (colorPrimary) => ({
    fontSize: 15,
    fontWeight: 700,
    color: '#1a1a1a',
    marginBottom: 18,
    paddingBottom: 10,
    borderBottom: `2px solid ${colorPrimary}22`,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
});

const labelStyle = {
    display: 'block',
    marginBottom: 5,
    fontSize: 12,
    color: Colors.LabelColor,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
};

const fieldGroup = { flex: 1, minWidth: 160 };

// ─── sub-component: client recipients table ───────────────────────────────────
const ClientRecipientsTable = ({ recipients, enabledReportTypes, onChange }) => {
    const { colorPrimary } = useColors();

    const handleEnabledToggle = (userId, checked) => {
        onChange(recipients.map(r =>
            r.id === userId ? { ...r, enabled: checked } : r
        ));
    };

    const handleReportTypesChange = (userId, types) => {
        // null = inherit all company types
        onChange(recipients.map(r =>
            r.id === userId ? { ...r, reportTypes: types.length === 0 ? null : types } : r
        ));
    };

    const columns = [
        {
            title: 'Name',
            render: (_, r) => (
                <div>
                    <div style={{ fontWeight: 600, color: '#1a1a1a', fontSize: 13 }}>
                        {r.firstName} {r.lastName}
                    </div>
                    <div style={{ color: Colors.LabelColor, fontSize: 12 }}>{r.email}</div>
                </div>
            ),
        },
        {
            title: 'Enabled',
            width: 90,
            align: 'center',
            render: (_, r) => (
                <Switch
                    size="small"
                    checked={r.enabled}
                    onChange={(v) => handleEnabledToggle(r.id, v)}
                    style={r.enabled ? { backgroundColor: colorPrimary } : {}}
                />
            ),
        },
        {
            title: (
                <span>
                    Report Types{' '}
                    <Tooltip title="Leave empty to inherit all company-enabled report types">
                        <InfoCircleOutlined style={{ color: Colors.LabelColor, fontSize: 12 }} />
                    </Tooltip>
                </span>
            ),
            render: (_, r) => (
                <Select
                    mode="multiple"
                    size="small"
                    style={{ width: '100%', minWidth: 200 }}
                    placeholder="All (inherit company types)"
                    value={r.reportTypes ?? []}
                    onChange={(v) => handleReportTypesChange(r.id, v)}
                    disabled={!r.enabled}
                    options={enabledReportTypes.map(t => ({
                        value: t,
                        label: REPORT_TYPE_LABELS[t] || t,
                    }))}
                    allowClear
                />
            ),
        },
    ];

    return (
        <Table
            size="small"
            columns={columns}
            dataSource={recipients}
            rowKey="id"
            pagination={false}
            style={{ marginTop: 8 }}
            scroll={{ x: 'max-content' }}
        />
    );
};

// ─── sub-component: run history table ────────────────────────────────────────
const RunHistoryTable = ({ runs, loading }) => {
    const formatDateTime = useDateTime();
    const columns = [
        {
            title: 'Report Date',
            dataIndex: 'reportDate',
            width: 120,
            render: v => v ? formatDateTime(v, 'date') : '—',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            width: 110,
            render: (v) => {
                const s = STATUS_MAP[v] || { color: '#999', icon: <MinusCircleOutlined />, label: v };
                return (
                    <Tag color={s.color} icon={s.icon} style={{ borderRadius: 20, fontWeight: 600 }}>
                        {s.label}
                    </Tag>
                );
            },
        },
        {
            title: 'Sent',
            dataIndex: 'sentCount',
            width: 70,
            align: 'center',
            render: v => <span style={{ color: '#52c41a', fontWeight: 600 }}>{v ?? 0}</span>,
        },
        {
            title: 'Failed',
            dataIndex: 'failedCount',
            width: 70,
            align: 'center',
            render: v => <span style={{ color: '#ff4d4f', fontWeight: 600 }}>{v ?? 0}</span>,
        },
        {
            title: 'Generated At',
            dataIndex: 'generatedAt',
            render: v => v ? formatDateTime(v) : '—',
        },
        {
            title: 'Scheduled For',
            dataIndex: 'scheduledFor',
            render: v => v ? formatDateTime(v) : '—',
        },
    ];

    return (
        <Table
            size="small"
            columns={columns}
            dataSource={runs}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 10, showSizeChanger: false }}
            style={{ marginTop: 8 }}
            locale={{ emptyText: 'No delivery history yet.' }}
            scroll={{ x: 'max-content' }}
        />
    );
};

// ─── main component ───────────────────────────────────────────────────────────
const ScheduledReportsSettings = () => {
    const { colorPrimary } = useColors();

    // ── settings state ──
    const [settingsLoading, setSettingsLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [enabled, setEnabled] = useState(false);
    const [time, setTime] = useState('08:00');
    const [timezone, setTimezone] = useState('UTC');
    const [reportTypes, setReportTypes] = useState([]);
    const [internalRoles, setInternalRoles] = useState([]);
    const [clientDeliveryEnabled, setClientDeliveryEnabled] = useState(false);

    // ── client recipients state ──
    const [recipientsLoading, setRecipientsLoading] = useState(false);
    const [recipientsSaveLoading, setRecipientsSaveLoading] = useState(false);
    const [recipients, setRecipients] = useState([]);

    // ── history state ──
    const [runsLoading, setRunsLoading] = useState(false);
    const [runs, setRuns] = useState([]);

    // ── load settings ──
    const loadSettings = useCallback(async () => {
        setSettingsLoading(true);
        try {
            const res = await ApiCaller.Get('/guard-company/scheduled-reports');
            const d = res?.data?.data;
            if (d) {
                setEnabled(!!d.scheduledReportsEnabled);
                setTime(d.scheduledReportsTime || '08:00');
                setTimezone(d.scheduledReportsTimezone || 'UTC');
                setReportTypes(d.reportTypes || []);
                setInternalRoles(d.internalRecipientRoles || []);
                setClientDeliveryEnabled(!!d.clientDeliveryEnabled);
            }
        } catch (err) {
            Utils.showMessage('error', err?.response?.data?.message || 'Failed to load scheduled report settings.');
        } finally {
            setSettingsLoading(false);
        }
    }, []);

    // ── load recipients ──
    const loadRecipients = useCallback(async () => {
        setRecipientsLoading(true);
        try {
            const res = await ApiCaller.Get('/guard-company/scheduled-reports/recipients');
            setRecipients(res?.data?.data || []);
        } catch (err) {
            Utils.showMessage('error', err?.response?.data?.message || 'Failed to load recipients.');
        } finally {
            setRecipientsLoading(false);
        }
    }, []);

    // ── load runs ──
    const loadRuns = useCallback(async () => {
        setRunsLoading(true);
        try {
            const res = await ApiCaller.Get('/guard-company/scheduled-reports/runs?sortBy=reportDate&sortOrder=desc&perPage=50');
            setRuns(res?.data?.data || []);
        } catch (err) {
            Utils.showMessage('error', err?.response?.data?.message || 'Failed to load delivery history.');
        } finally {
            setRunsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadSettings();
        loadRuns();
    }, [loadSettings, loadRuns]);

    useEffect(() => {
        if (clientDeliveryEnabled) {
            loadRecipients();
        }
    }, [clientDeliveryEnabled, loadRecipients]);

    // ── validate + save settings ──
    const handleSaveSettings = async () => {
        // FE validation
        if (enabled) {
            if (!time) {
                Utils.showMessage('error', 'Please select a delivery time.');
                return;
            }
            if (!timezone) {
                Utils.showMessage('error', 'Please select a timezone.');
                return;
            }
            if (reportTypes.length === 0) {
                Utils.showMessage('error', 'Please select at least one report type.');
                return;
            }
            if (internalRoles.length === 0 && !clientDeliveryEnabled) {
                Utils.showMessage('error', 'Please select at least one internal role or enable client delivery.');
                return;
            }
        }

        const payload = { scheduledReportsEnabled: enabled };
        if (enabled) {
            payload.scheduledReportsTime = time;
            payload.scheduledReportsTimezone = timezone;
            payload.reportTypes = reportTypes;
            payload.internalRecipientRoles = internalRoles;
            payload.clientDeliveryEnabled = clientDeliveryEnabled;
        }

        setSaveLoading(true);
        try {
            const res = await ApiCaller.Patch('/guard-company/scheduled-reports', payload);
            Utils.showMessage('success', res?.data?.message || 'Scheduled report settings saved.');
            await loadSettings();
        } catch (err) {
            Utils.showMessage('error', err?.response?.data?.message || 'Failed to save settings.');
        } finally {
            setSaveLoading(false);
        }
    };

    // ── save recipients ──
    const handleSaveRecipients = async () => {
        const payload = {
            recipients: recipients.map(r => ({
                userId: r.id,
                enabled: r.enabled,
                reportTypes: r.reportTypes ?? null,
            })),
        };
        setRecipientsSaveLoading(true);
        try {
            const res = await ApiCaller.Patch('/guard-company/scheduled-reports/recipients', payload);
            Utils.showMessage('success', res?.data?.message || 'Client recipients saved.');
        } catch (err) {
            Utils.showMessage('error', err?.response?.data?.message || 'Failed to save recipients.');
        } finally {
            setRecipientsSaveLoading(false);
        }
    };

    // ── last run badge ──
    const lastRun = runs[0];
    const lastSuccess = runs.find(r => r.status === 'sent');

    return (
        <Spin spinning={settingsLoading} tip="Loading...">
            <div style={{ width: '100%' }}>

                {/* ── status summary strip ── */}
                {(lastRun || lastSuccess) && (
                    <div style={{
                        display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20,
                        padding: '12px 18px', background: '#f7f9fc', borderRadius: 8,
                        border: '1px solid #e0e4e8',
                    }}>
                        {lastRun && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 12, color: Colors.LabelColor, fontWeight: 600 }}>LAST RUN</span>
                                <Tag
                                    color={STATUS_MAP[lastRun.status]?.color || '#999'}
                                    icon={STATUS_MAP[lastRun.status]?.icon}
                                    style={{ borderRadius: 20 }}
                                >
                                    {STATUS_MAP[lastRun.status]?.label || lastRun.status}
                                </Tag>
                                <span style={{ fontSize: 12, color: Colors.LabelColor }}>
                                    {lastRun.reportDate}
                                </span>
                            </div>
                        )}
                        {lastSuccess && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 12, color: Colors.LabelColor, fontWeight: 600 }}>LAST SUCCESS</span>
                                <span style={{ fontSize: 12, color: '#52c41a', fontWeight: 600 }}>
                                    {lastSuccess.reportDate} · {lastSuccess.sentCount} sent
                                </span>
                            </div>
                        )}
                    </div>
                )}

                {/* ── 1. master toggle ── */}
                <div style={card}>
                    <div style={sectionTitle(colorPrimary)}>
                        <MailOutlined style={{ color: colorPrimary }} />
                        Scheduled Email Reports
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: enabled ? 24 : 0 }}>
                        <div>
                            <p style={{ fontWeight: 600, color: '#1a1a1a', margin: 0, fontSize: 14 }}>
                                Enable Scheduled Email Reports
                            </p>
                            <p style={{ color: Colors.LabelColor, fontSize: 12, margin: '4px 0 0' }}>
                                Reports are automatically generated and emailed by the backend scheduler.
                            </p>
                        </div>
                        <Switch
                            checked={enabled}
                            onChange={setEnabled}
                            style={enabled ? { backgroundColor: colorPrimary } : {}}
                        />
                    </div>

                    {enabled && (
                        <>
                            {/* time + timezone row */}
                            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 20 }}>
                                <div style={fieldGroup}>
                                    <label style={labelStyle}>
                                        <ClockCircleOutlined style={{ marginRight: 4 }} />
                                        Delivery Time
                                    </label>
                                    <Select
                                        showSearch
                                        value={time}
                                        onChange={setTime}
                                        options={TIME_OPTIONS}
                                        style={{ width: '100%' }}
                                        placeholder="HH:MM"
                                        filterOption={(input, opt) => opt.value.includes(input)}
                                    />
                                </div>
                                <div style={fieldGroup}>
                                    <label style={labelStyle}>
                                        <GlobalOutlined style={{ marginRight: 4 }} />
                                        Timezone
                                    </label>
                                    <Select
                                        showSearch
                                        value={timezone}
                                        onChange={setTimezone}
                                        style={{ width: '100%' }}
                                        placeholder="Select timezone"
                                        options={TIMEZONES.map(tz => ({ value: tz, label: tz }))}
                                        filterOption={(input, opt) =>
                                            opt.value.toLowerCase().includes(input.toLowerCase())
                                        }
                                    />
                                </div>
                            </div>

                            {/* report types */}
                            <div style={{ marginBottom: 20 }}>
                                <label style={labelStyle}>Report Types (select at least one)</label>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 24px', marginTop: 6 }}>
                                    {ALL_REPORT_TYPES.map(type => (
                                        <Checkbox
                                            key={type}
                                            checked={reportTypes.includes(type)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setReportTypes(prev => [...prev, type]);
                                                } else {
                                                    setReportTypes(prev => prev.filter(t => t !== type));
                                                }
                                            }}
                                        >
                                            <span style={{ fontWeight: 500, color: '#1a1a1a' }}>
                                                {REPORT_TYPE_LABELS[type]}
                                            </span>
                                        </Checkbox>
                                    ))}
                                </div>
                            </div>

                            {/* internal recipient roles */}
                            <div style={{ marginBottom: 20 }}>
                                <label style={labelStyle}>Internal Recipient Roles</label>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 24px', marginTop: 6 }}>
                                    {ALL_ROLES.map(role => (
                                        <Checkbox
                                            key={role}
                                            checked={internalRoles.includes(role)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setInternalRoles(prev => [...prev, role]);
                                                } else {
                                                    setInternalRoles(prev => prev.filter(r => r !== role));
                                                }
                                            }}
                                        >
                                            <span style={{ fontWeight: 500, color: '#1a1a1a' }}>
                                                {ROLE_LABELS[role]}
                                            </span>
                                        </Checkbox>
                                    ))}
                                </div>
                                <p style={{ fontSize: 11, color: Colors.LabelColor, marginTop: 6 }}>
                                    Select at least one internal role <em>or</em> enable client delivery below.
                                </p>
                            </div>

                            {/* client delivery toggle */}
                            <div style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '14px 18px', background: clientDeliveryEnabled ? `${colorPrimary}0d` : '#f7f9fc',
                                borderRadius: 8, border: `1px solid ${clientDeliveryEnabled ? colorPrimary + '33' : '#e0e4e8'}`,
                                transition: 'all 0.2s',
                            }}>
                                <div>
                                    <p style={{ fontWeight: 600, color: '#1a1a1a', margin: 0, fontSize: 13 }}>
                                        Send to Clients
                                    </p>
                                    <p style={{ color: Colors.LabelColor, fontSize: 12, margin: '2px 0 0' }}>
                                        Enable to also deliver reports to client users.
                                    </p>
                                </div>
                                <Switch
                                    checked={clientDeliveryEnabled}
                                    onChange={(v) => {
                                        setClientDeliveryEnabled(v);
                                        if (v && recipients.length === 0) loadRecipients();
                                    }}
                                    style={clientDeliveryEnabled ? { backgroundColor: colorPrimary } : {}}
                                />
                            </div>
                        </>
                    )}
                </div>

                {/* ── 2. save settings button ── */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24 }}>
                    <Button
                        type="primary"
                        loading={saveLoading}
                        onClick={handleSaveSettings}
                        style={{
                            background: colorPrimary, border: 'none', borderRadius: 6,
                            height: 40, padding: '0 36px', fontWeight: 600, fontSize: 14,
                        }}
                    >
                        Save Report Settings
                    </Button>
                </div>

                {/* ── 3. client recipients (only when enabled + clientDeliveryEnabled) ── */}
                {enabled && clientDeliveryEnabled && (
                    <div style={card}>
                        <div style={sectionTitle(colorPrimary)}>
                            <MailOutlined style={{ color: colorPrimary }} />
                            Client Recipients
                            <Badge count={recipients.filter(r => r.enabled).length} showZero
                                style={{ backgroundColor: colorPrimary, marginLeft: 6 }} />
                        </div>

                        <p style={{ fontSize: 12, color: Colors.LabelColor, marginBottom: 14 }}>
                            Toggle individual clients on/off and optionally restrict which report types they receive.
                            Leave <em>Report Types</em> empty to inherit all company-enabled types.
                        </p>

                        {recipientsLoading ? (
                            <div style={{ textAlign: 'center', padding: 30 }}><Spin /></div>
                        ) : recipients.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: 30, color: Colors.LabelColor }}>
                                No client users found in this company.
                            </div>
                        ) : (
                            <ClientRecipientsTable
                                recipients={recipients}
                                enabledReportTypes={reportTypes}
                                onChange={setRecipients}
                            />
                        )}

                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                            <Button
                                type="primary"
                                loading={recipientsSaveLoading}
                                onClick={handleSaveRecipients}
                                disabled={recipients.length === 0}
                                style={{
                                    background: colorPrimary, border: 'none', borderRadius: 6,
                                    height: 38, padding: '0 28px', fontWeight: 600, fontSize: 13,
                                }}
                            >
                                Save Recipients
                            </Button>
                        </div>
                    </div>
                )}

                {/* ── 4. delivery / run history ── */}
                <div style={card}>
                    <div style={{ ...sectionTitle(colorPrimary), justifyContent: 'space-between' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <ClockCircleOutlined style={{ color: colorPrimary }} />
                            Delivery History
                        </span>
                        <Button
                            size="small"
                            icon={<SyncOutlined />}
                            onClick={loadRuns}
                            loading={runsLoading}
                            style={{ fontWeight: 500 }}
                        >
                            Refresh
                        </Button>
                    </div>

                    <RunHistoryTable runs={runs} loading={runsLoading} />
                </div>

            </div>
        </Spin>
    );
};

export default ScheduledReportsSettings;
